import { create } from 'zustand';
import { toast } from 'sonner';
import {
    brandApprovalService,
    BrandApproval,
    BrandApprovalStats,
    BrandApprovalFilters,
    CreateBrandApprovalDto,
    UpdateBrandApprovalDto,
    AuditLogEntry,
} from '../services/brandApproval.service';

type ActiveTab = 'pending' | 'in_review' | 'approved' | 'all';

interface BrandApprovalState {
    // Data
    approvals: BrandApproval[];
    selectedApproval: BrandApproval | null;
    stats: BrandApprovalStats | null;
    auditLog: AuditLogEntry[];

    // UI State
    isLoading: boolean;
    activeTab: ActiveTab;
    filters: BrandApprovalFilters;
    searchTerm: string;

    // Actions - Approvals
    fetchApprovals: (filters?: BrandApprovalFilters) => Promise<void>;
    fetchApproval: (id: string) => Promise<void>;
    createApproval: (data: CreateBrandApprovalDto) => Promise<BrandApproval>;
    updateApproval: (id: string, data: UpdateBrandApprovalDto) => Promise<void>;
    deleteApproval: (id: string) => Promise<void>;
    setSelectedApproval: (approval: BrandApproval | null) => void;

    // Actions - Version & Portal
    submitNewVersion: (id: string, data: { previewUrl?: string; attachments?: string[]; notes?: string }) => Promise<void>;
    regeneratePortalLink: (id: string) => Promise<void>;
    sendReminder: (id: string) => Promise<void>;

    // Actions - Change Requests
    updateChangeRequest: (approvalId: string, requestId: string, data: { status?: string; assignedTo?: string; internalNotes?: string; resolution?: string }) => Promise<void>;

    // Actions - Stats & Audit
    fetchStats: () => Promise<void>;
    fetchAuditLog: (id: string) => Promise<void>;

    // Actions - UI
    setActiveTab: (tab: ActiveTab) => void;
    setFilters: (filters: Partial<BrandApprovalFilters>) => void;
    setSearchTerm: (term: string) => void;

    // Helpers
    getFilteredApprovals: () => BrandApproval[];
}

export const useBrandApprovalStore = create<BrandApprovalState>((set, get) => ({
    // Initial State
    approvals: [],
    selectedApproval: null,
    stats: null,
    auditLog: [],
    isLoading: false,
    activeTab: 'pending',
    filters: {},
    searchTerm: '',

    // Approvals Actions
    fetchApprovals: async (filters?: BrandApprovalFilters) => {
        try {
            set({ isLoading: true });
            const approvals = await brandApprovalService.getApprovals(filters || get().filters);
            set({ approvals });
        } catch (error) {
            console.error('Failed to fetch brand approvals:', error);
            toast.error('Failed to load brand approvals');
        } finally {
            set({ isLoading: false });
        }
    },

    fetchApproval: async (id: string) => {
        try {
            set({ isLoading: true });
            const approval = await brandApprovalService.getApproval(id);
            set({ selectedApproval: approval });
        } catch (error) {
            console.error('Failed to fetch brand approval:', error);
            toast.error('Failed to load brand approval details');
        } finally {
            set({ isLoading: false });
        }
    },

    createApproval: async (data: CreateBrandApprovalDto) => {
        try {
            set({ isLoading: true });
            const approval = await brandApprovalService.createApproval(data);
            set((state) => ({ approvals: [approval, ...state.approvals] }));
            toast.success('Brand approval request created');
            // Refresh stats
            get().fetchStats();
            return approval;
        } catch (error) {
            console.error('Failed to create brand approval:', error);
            toast.error('Failed to create brand approval');
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    updateApproval: async (id: string, data: UpdateBrandApprovalDto) => {
        try {
            const updated = await brandApprovalService.updateApproval(id, data);
            set((state) => ({
                approvals: state.approvals.map((a) => (a.id === id ? updated : a)),
                selectedApproval: state.selectedApproval?.id === id ? updated : state.selectedApproval,
            }));
            toast.success('Brand approval updated');
        } catch (error) {
            console.error('Failed to update brand approval:', error);
            toast.error('Failed to update brand approval');
            throw error;
        }
    },

    deleteApproval: async (id: string) => {
        try {
            await brandApprovalService.deleteApproval(id);
            set((state) => ({
                approvals: state.approvals.filter((a) => a.id !== id),
                selectedApproval: state.selectedApproval?.id === id ? null : state.selectedApproval,
            }));
            toast.success('Brand approval deleted');
            get().fetchStats();
        } catch (error) {
            console.error('Failed to delete brand approval:', error);
            toast.error('Failed to delete brand approval');
        }
    },

    setSelectedApproval: (approval) => set({ selectedApproval: approval }),

    // Version & Portal Actions
    submitNewVersion: async (id, data) => {
        try {
            const updated = await brandApprovalService.submitNewVersion(id, data);
            set((state) => ({
                approvals: state.approvals.map((a) => (a.id === id ? updated : a)),
                selectedApproval: state.selectedApproval?.id === id ? updated : state.selectedApproval,
            }));
            toast.success('New version submitted for review');
            get().fetchStats();
        } catch (error) {
            console.error('Failed to submit new version:', error);
            toast.error('Failed to submit new version');
        }
    },

    regeneratePortalLink: async (id) => {
        try {
            const updated = await brandApprovalService.regeneratePortalLink(id);
            set((state) => ({
                approvals: state.approvals.map((a) => (a.id === id ? updated : a)),
                selectedApproval: state.selectedApproval?.id === id ? updated : state.selectedApproval,
            }));
            toast.success('Portal link regenerated');
        } catch (error) {
            console.error('Failed to regenerate portal link:', error);
            toast.error('Failed to regenerate portal link');
        }
    },

    sendReminder: async (id) => {
        try {
            await brandApprovalService.sendReminder(id);
            toast.success('Reminder sent to brand contact');
        } catch (error) {
            console.error('Failed to send reminder:', error);
            toast.error('Failed to send reminder');
        }
    },

    // Change Request Actions
    updateChangeRequest: async (approvalId, requestId, data) => {
        try {
            const updated = await brandApprovalService.updateChangeRequest(approvalId, requestId, data);
            set((state) => ({
                approvals: state.approvals.map((a) => (a.id === approvalId ? updated : a)),
                selectedApproval: state.selectedApproval?.id === approvalId ? updated : state.selectedApproval,
            }));
            toast.success('Change request updated');
        } catch (error) {
            console.error('Failed to update change request:', error);
            toast.error('Failed to update change request');
        }
    },

    // Stats & Audit Actions
    fetchStats: async () => {
        try {
            const stats = await brandApprovalService.getStats();
            set({ stats });
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    },

    fetchAuditLog: async (id: string) => {
        try {
            const auditLog = await brandApprovalService.getAuditLog(id);
            set({ auditLog });
        } catch (error) {
            console.error('Failed to fetch audit log:', error);
            toast.error('Failed to load audit log');
        }
    },

    // UI Actions
    setActiveTab: (tab) => set({ activeTab: tab }),
    setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    setSearchTerm: (term) => set({ searchTerm: term }),

    // Helpers
    getFilteredApprovals: () => {
        const { approvals, searchTerm, activeTab } = get();

        return approvals.filter((approval) => {
            // Tab filter
            if (activeTab === 'pending' && approval.status !== 'pending_brand_review') return false;
            if (activeTab === 'in_review' && approval.status !== 'in_brand_review') return false;
            if (activeTab === 'approved' && !['approved', 'approved_with_changes'].includes(approval.status)) return false;

            // Search filter
            if (searchTerm) {
                const search = searchTerm.toLowerCase();
                return (
                    approval.title.toLowerCase().includes(search) ||
                    approval.brandContact.company.toLowerCase().includes(search) ||
                    approval.brandContact.name.toLowerCase().includes(search)
                );
            }
            return true;
        });
    },
}));
